<template>
  <div class="ad-banners" v-if="banners.length > 0">
    <section
        v-for="banner in banners"
        :key="banner.id"
        class="ad-banner"
        :style="bannerStyle(banner)"
    >
      <div class="ad-banner__viewport">
        <article
            v-for="(slide, index) in sortedSlides(banner)"
            :key="slide.id"
            class="ad-banner__slide"
            :class="{ active: activeSlides[banner.id] === index }"
            :style="slideStyle(slide)"
        >
          <div class="ad-banner__content">
            <h2 v-if="slide.title">{{ slide.title }}</h2>
            <p>{{ slide.text }}</p>

            <a
                v-if="slide.button_text && slide.button_url"
                class="ad-banner__button"
                :href="slide.button_url"
                target="_blank"
                rel="noopener noreferrer"
            >
              {{ slide.button_text }}
            </a>
          </div>
        </article>
      </div>

      <div v-if="sortedSlides(banner).length > 1" class="ad-banner__dots">
        <button
            v-for="(slide, index) in sortedSlides(banner)"
            :key="slide.id"
            type="button"
            :class="{ active: activeSlides[banner.id] === index }"
            @click="setActiveSlide(banner.id, index)"
        ></button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdBannerCarousel',
  props: {
    targetGroup: {
      type: String,
      required: true,
      validator(value) {
        return ['clients', 'specialists'].includes(value);
      },
    },
    placementId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      banners: [],
      activeSlides: {},
      intervals: [],
    };
  },
  mounted() {
    this.getBanners();
  },
  beforeUnmount() {
    this.intervals.forEach(interval => clearInterval(interval));
  },
  methods: {
    getBanners() {
      let entryMode = localStorage.getItem('m2vetEntryMode') || 'do wszystkich';

      axios.get(`/ad-banners/${this.placementId}`, {
        params: {
          target_group: entryMode === 'do wszystkich' ? 'clients,specialists' : (entryMode === 'do specjalistów' ? 'specialists' : 'clients'),
        },
      }).then(res => {
        this.banners = Array.isArray(res.data) ? res.data : (res.data.data || []);

        const activeSlides = {};
        this.banners.forEach(banner => {
          activeSlides[banner.id] = 0;
        });
        this.activeSlides = activeSlides;

        this.startAutoplay();
      }).catch(() => {
        this.banners = [];
      });
    },
    startAutoplay() {
      this.intervals.forEach(interval => clearInterval(interval));
      this.intervals = [];

      this.banners.forEach(banner => {
        const slides = this.sortedSlides(banner);

        if (slides.length > 1) {
          const interval = setInterval(() => {
            const current = this.activeSlides[banner.id] || 0;
            const next = current + 1 >= slides.length ? 0 : current + 1;
            this.setActiveSlide(banner.id, next);
          }, 5000);

          this.intervals.push(interval);
        }
      });
    },
    sortedSlides(banner) {
      return [...(banner.slides || [])].sort((a, b) => Number(a.order) - Number(b.order));
    },
    setActiveSlide(bannerId, index) {
      this.activeSlides = {
        ...this.activeSlides,
        [bannerId]: index,
      };
    },
    bannerStyle(banner) {
      const options = banner.options || {};
      return {
        maxWidth: options.width ? `${options.width}px` : '1200px',
        minHeight: options.height ? `${options.height}px` : '300px',
      };
    },
    slideStyle(slide) {
      const background = slide.options && slide.options.background
          ? slide.options.background
          : {};

      if (background.type === 'image' && background.image_path) {
        return {
          backgroundImage: `linear-gradient(rgba(17, 24, 39, .35), rgba(17, 24, 39, .35)), url("${process.env.VUE_APP_STORAGE_URL}${background.image_path}")`,
        };
      }

      if (background.type === 'gradient' && background.color) {
        return {
          background: background.color,
        };
      }

      return {
        background: background.color || '#10b981',
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ad-banner {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #f3f4f6;
  box-shadow: 0 10px 30px rgba(15, 23, 42, .12);
}

.ad-banner + .ad-banner {
  margin-top: 20px;
}

.ad-banner__viewport {
  position: relative;
  min-height: inherit;
}

.ad-banner__slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity .35s ease;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
}

.ad-banner__slide.active {
  position: relative;
  opacity: 1;
  pointer-events: auto;
  min-height: inherit;
}

.ad-banner__content {
  width: min(680px, calc(100% - 32px));
  margin: 0 auto;
  color: #fff;
  text-align: center;
  padding: 32px 16px;
  box-sizing: border-box;
}

.ad-banner__content h2 {
  margin: 0 0 10px;
  font-size: clamp(2.4rem, 4vw, 4rem);
  line-height: 1.1;
}

.ad-banner__content p {
  margin: 0;
  font-size: clamp(1.5rem, 2vw, 2rem);
  line-height: 1.5;
}

.ad-banner__button {
  display: inline-flex;
  margin-top: 18px;
  background: #f97316;
  color: #fff;
  text-decoration: none;
  border-radius: 999px;
  padding: 11px 22px;
  font-size: 1.5rem;
  font-weight: 700;
}

.ad-banner__button:hover {
  background: #ea6c0a;
}

.ad-banner__dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.ad-banner__dots button {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  border: none;
  background: rgba(255, 255, 255, .6);
  cursor: pointer;
}

.ad-banner__dots button.active {
  background: #fff;
}

@media (max-width: 640px) {
  .ad-banners {
    width: calc(100% - 24px);
  }

  .ad-banner__content {
    padding: 24px 12px 40px;
  }
}
</style>